import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Button } from './common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import LoadingSpinner from './LoadingSpinner';
import { reservationService } from './clients/ApiClients';
import { ReservationSeriesCancelPreviewReturn, ReservationSeriesDetail } from './clients/rust-api-client';
import { canManageReservations } from './constants/UserTypes';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { formatMoney } from './utils/billingHelpers';
import { ReservationRow } from './reservation/ReservationRow';
import { describeSeries, seriesCancelTotals } from './reservation/seriesMeta';
import {
  ACTIVE_STATUSES,
  ReservationStatus,
  cancelOutcomeLabel,
  formatLocalRange,
  formatMoment,
  reservationErrorMessage,
} from './reservation/reservationMeta';
import {
  checkboxClass,
  inputClass,
  labelClass,
} from './reservation/formStyles';
import { ErrorAlert } from './common/Custom/ErrorAlert';

/**
 * One recurring series (P2, #642): its rule, and every booking it made.
 * Cancelling the series cancels the occurrences still to come — the ones
 * already past stay as history — with the fee rules of a manager cancel.
 */
const ReservationSeriesPage: React.FC = () => {
  const { hoaId, seriesId } = useParams<{ hoaId: string; seriesId: string }>();
  const navigate = useNavigate();
  const canManage = canManageReservations(getUserTypeFromToken());
  const [detail, setDetail] = useState<ReservationSeriesDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!seriesId) return;
    let live = true;
    setLoading(true);
    reservationService
      .getReservationSeries({ seriesId })
      .then((r) => live && setDetail(r.data))
      .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't load the series.")))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [seriesId]);

  if (loading) return <LoadingSpinner />;

  const s = detail?.series;
  const rows = [...(detail?.reservations ?? [])].sort((a, b) => a.start_local.localeCompare(b.start_local));
  const upcoming = rows.filter((r) => ACTIVE_STATUSES.includes(r.status as ReservationStatus));
  const first = rows[0];

  return (
    <SidebarLayout sidebar={<AppSidebar currentPage="reservations" />} navbar={<Navbar>{/* Your navbar content */}</Navbar>}>
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to={`/hoa/${hoaId}/reservations`} className="shrink-0 text-fg-muted hover:text-fg">
          Reservations
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="min-w-0 truncate font-medium text-fg">Recurring series</span>
      </div>

      <ErrorAlert>{error}</ErrorAlert>

      {s && (
        <div className="space-y-6">
          <div>
            <p className="text-[13px] font-medium text-fg-muted">
              {s.cancelled_at ? `Cancelled ${formatMoment(s.cancelled_at)}` : 'Recurring series'}
            </p>
            <h1 className="mt-1 text-2xl/8 font-semibold tracking-tight text-fg">
              {first ? `${first.amenity_name} · ${first.home_address}` : 'Recurring series'}
            </h1>
            <p className="mt-2 text-[15px]/6 text-fg-secondary">{describeSeries(s)}</p>
            {s.purpose && <p className="mt-2 text-[13px] text-fg-secondary">{s.purpose}</p>}
            {s.cancel_reason && (
              <p className="mt-2 rounded-md border border-line px-3 py-2 text-[13px] text-fg-secondary">
                {s.cancel_reason}
              </p>
            )}
            <p className="mt-2 text-[12px] text-fg-muted">
              {rows.length} booking{rows.length === 1 ? '' : 's'} · {upcoming.length} still to come
              {s.date_created && ` · created ${formatMoment(s.date_created)}`}
            </p>
          </div>

          {canManage && !s.cancelled_at && upcoming.length > 0 && (
            <Button outline onClick={() => setCancelling(true)}>
              Cancel the rest of the series
            </Button>
          )}

          <section aria-label="Bookings in this series" className="rounded-lg border border-line">
            {rows.length === 0 ? (
              <p className="px-4 py-6 text-center text-[13px] text-fg-muted">No bookings.</p>
            ) : (
              <ul className="divide-y divide-line/50">
                {rows.map((r) => (
                  <ReservationRow
                    key={r.id}
                    reservation={r}
                    hideHome
                    onOpen={(x) => navigate(`/hoa/${x.hoa_id}/reservations/${x.id}`)}
                  />
                ))}
              </ul>
            )}
          </section>

          <CancelSeriesDialog
            open={cancelling}
            seriesId={s.id}
            upcoming={upcoming.length}
            onClose={() => setCancelling(false)}
            onDone={setDetail}
          />
        </div>
      )}
    </SidebarLayout>
  );
};

const CancelSeriesDialog: React.FC<{
  open: boolean;
  seriesId: string;
  upcoming: number;
  onClose: () => void;
  onDone: (d: ReservationSeriesDetail) => void;
}> = ({ open, seriesId, upcoming, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [reverseFee, setReverseFee] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReservationSeriesCancelPreviewReturn | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setReverseFee(true);
      setError(null);
    }
  }, [open]);

  // What cancelling would do to each booking's fee — previewed (it writes
  // nothing) whenever the reverse-fees choice changes.
  useEffect(() => {
    setPreview(null);
    if (!open) return;
    let live = true;
    reservationService
      .previewReservationSeriesCancel({
        seriesId,
        reservationSeriesCancelPreview: { reverse_fee: reverseFee },
      })
      .then((res) => live && setPreview(res.data))
      .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't preview the cancellation.")));
    return () => {
      live = false;
    };
  }, [open, seriesId, reverseFee]);

  const totals = preview ? seriesCancelTotals(preview, formatMoney) : [];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await reservationService.cancelReservationSeries({
        seriesId,
        reservationSeriesCancel: { reason: reason.trim(), reverse_fee: reverseFee },
      });
      onDone(res.data);
      onClose();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't cancel the series."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>Cancel the rest of the series</DialogTitle>
        <DialogDescription>
          {upcoming} booking{upcoming === 1 ? '' : 's'} still to come will be cancelled. Past ones stay as history.
        </DialogDescription>
        <DialogBody className="space-y-4">
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              className={checkboxClass}
              checked={reverseFee}
              onChange={(e) => setReverseFee(e.target.checked)}
            />
            Reverse their fees
          </label>
          {preview && preview.occurrences.length > 0 && (
            <div className="space-y-1.5">
              <ul className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-line px-3 py-2 text-[13px]">
                {preview.occurrences.map((o) => (
                  <li key={o.reservation_id} className="flex flex-wrap justify-between gap-x-3 text-fg-secondary">
                    <span className="tabular-nums">{formatLocalRange(o.start_local, o.end_local)}</span>
                    <span className="text-fg-muted">
                      {cancelOutcomeLabel(o.outcome.outcome)}
                      {o.outcome.outcome === 'late_fee' && ` (${formatMoney(o.outcome.charge)})`}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-[13px] text-fg-secondary">
                {totals.length ? `In all: ${totals.join(' · ')}.` : 'No money moves.'}
              </p>
            </div>
          )}
          {preview && preview.already_cancelled && (
            <p className="text-[13px] text-fg-muted">This series has already been cancelled.</p>
          )}
          <label className={labelClass}>
            Reason (required — the household sees it)
            <textarea
              rows={3}
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={clsx(inputClass, 'resize-none')}
            />
          </label>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Keep the series
          </Button>
          <Button color="red" type="submit" disabled={saving || !reason.trim()}>
            {saving ? 'Cancelling…' : 'Cancel the rest'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ReservationSeriesPage;
